import React, { useEffect, useMemo, useState } from "react";
import StarRating from "../common/StarRating";
import { useMutation } from "@apollo/client";
import { ADD_REFEREE_TO_STAGE_MUTATION } from "./mutation/AddRefereeToStage";
import { GlobalStyles } from "../../constants/styles";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";

export default function RefereeList({
                                        referees = [],
                                        stageReferees = [],
                                        onSelectionChange,
                                        stageId,
                                        scheduleType,
                                    }) {
    const { t } = useTranslation();
    const [availableReferees, setAvailableReferees] = useState(referees);
    const [selectedReferees, setSelectedReferees] = useState(stageReferees);
    const [searchQuery, setSearchQuery] = useState("");
    const [addRemoveReferee] = useMutation(ADD_REFEREE_TO_STAGE_MUTATION);
    const colors = useThemeColors();

    const coachLabel =
        scheduleType === "TRAINING"
            ? t("refereeList.roles.coaches")
            : t("refereeList.roles.refereesOrUmpires");

    useEffect(() => {
        setAvailableReferees(referees || []);
    }, [referees]);

    useEffect(() => {
        setSelectedReferees(stageReferees || []);
    }, [stageReferees]);

    const ui = useMemo(() => {
        return {
            wrap: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                boxShadow: "0 18px 44px rgba(0,0,0,0.10)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
            },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 14,
                fontWeight: 900,
                color: colors.textOnPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            sub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textOnPrimary,
            },
            body: { padding: 14 },
            columns: {
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
            },
            colCard: {
                borderRadius: 14,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.06)",
                background: "#fff",
                boxShadow: "0 10px 26px rgba(0,0,0,0.06)",
            },
            colHeader: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 12px",
                background: "rgba(0,0,0,0.02)",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            colTitleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 8,
                minWidth: 0,
            },
            colTitle: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 900,
                color: "rgba(20,21,26,0.72)",
                textTransform: "uppercase",
                letterSpacing: "0.02em",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            smallBadge: {
                fontSize: 12,
                fontWeight: 900,
                padding: "5px 9px",
                borderRadius: 999,
                background: colors.secondary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnSecondary,
                whiteSpace: "nowrap",
            },
            colBody: { padding: 12 },
            search: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: "10px 12px",
                width: "100%",
                fontWeight: 700,
                outline: "none",
            },
            list: {
                marginTop: 10,
                maxHeight: 320,
                overflowY: "auto",
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.01)",
            },
            row: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                padding: "10px 12px",
                borderTop: "1px solid rgba(0,0,0,0.06)",
            },
            rowFirst: {
                borderTop: "none",
            },
            name: {
                fontSize: 13.5,
                fontWeight: 900,
                color: "#14151a",
                margin: 0,
            },
            muted: {
                fontSize: 12.5,
                fontWeight: 700,
                color: "rgba(20,21,26,0.55)",
            },
            btn: {
                borderRadius: 12,
                padding: "9px 10px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                whiteSpace: "nowrap",
            },
            btnAdd: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
            btnRemove: {
                border: "1px solid rgba(220,53,69,0.35)",
                background: "rgba(220,53,69,0.06)",
                color: "#dc3545",
            },
            empty: {
                padding: 12,
                color: "rgba(20,21,26,0.55)",
                fontWeight: 700,
            },
            helper: {
                marginTop: 10,
                fontSize: 12.5,
                fontWeight: 700,
                color: "rgba(20,21,26,0.55)",
            },
        };
    }, [colors.primary, colors.secondary, colors.textOnPrimary, colors.textOnSecondary]);

    const filteredReferees = useMemo(() => {
        const q = (searchQuery || "").trim().toLowerCase();
        if (!q) return availableReferees;
        return (availableReferees || []).filter((referee) =>
            (referee.fullName || "").toLowerCase().includes(q)
        );
    }, [availableReferees, searchQuery]);

    const selectReferee = async (referee) => {
        try {
            await addRemoveReferee({
                variables: {
                    stageId: parseInt(stageId, 10),
                    refereeId: parseInt(referee.id, 10),
                },
            });

            setAvailableReferees((prev) => prev.filter((r) => r.id !== referee.id));
            setSelectedReferees((prev) => {
                const updated = [...prev, referee];
                onSelectionChange?.(updated);
                return updated;
            });
        } catch (error) {
            console.log("Operation Error", error);
        }
    };

    const removeReferee = async (referee) => {
        try {
            await addRemoveReferee({
                variables: {
                    stageId: parseInt(stageId, 10),
                    refereeId: parseInt(referee.id, 10),
                    action: t("refereeList.actions.remove"),
                },
            });

            setSelectedReferees((prev) => {
                const updated = prev.filter((r) => r.id !== referee.id);
                onSelectionChange?.(updated);
                return updated;
            });

            setAvailableReferees((prev) => [...prev, referee]);
        } catch (error) {
            console.log("Operation Error", error);
        }
    };

    return (
        <>
            <div id="kbola-referee-list" style={ui.wrap}>
                <div style={ui.header}>
                    <div style={ui.titleWrap}>
                        <span style={ui.badge}>{selectedReferees?.length || 0}</span>
                        <div style={{ minWidth: 0 }}>
                            <h3 style={ui.title}>{coachLabel.toUpperCase()}</h3>
                            <p style={ui.sub}>
                                {t("refereeList.header.chooseForStage", { role: coachLabel })}
                            </p>
                        </div>
                    </div>
                </div>

                <div style={ui.body}>
                    <div style={ui.columns} className="kbola-grid">
                        <div style={ui.colCard}>
                            <div style={ui.colHeader}>
                                <div style={ui.colTitleWrap}>
                                    <h4 style={ui.colTitle}>
                                        {t("refereeList.sections.available", { role: coachLabel })}
                                    </h4>
                                    <span style={ui.smallBadge}>
                                        {filteredReferees?.length || 0}
                                    </span>
                                </div>
                            </div>

                            <div style={ui.colBody}>
                                <input
                                    type="text"
                                    placeholder={t("refereeList.search.placeholder", {
                                        role: coachLabel,
                                    })}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={ui.search}
                                />

                                <div style={ui.list}>
                                    {filteredReferees?.length ? (
                                        filteredReferees.map((referee, idx) => (
                                            <div
                                                key={referee.id}
                                                style={{
                                                    ...ui.row,
                                                    ...(idx === 0 ? ui.rowFirst : null),
                                                }}
                                            >
                                                <div style={{ minWidth: 0 }}>
                                                    <p style={ui.name}>{referee.fullName}</p>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 8,
                                                        }}
                                                    >
                                                        <StarRating rating={referee.rating} />
                                                        <span style={ui.muted}>
                                                            {t("refereeList.labels.rating")}
                                                        </span>
                                                    </div>
                                                </div>

                                                <button
                                                    type="button"
                                                    style={{ ...ui.btn, ...ui.btnAdd }}
                                                    onClick={() => selectReferee(referee)}
                                                >
                                                    {t("refereeList.buttons.add")}
                                                    <i className="fas fa-chevron-right" />
                                                </button>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={ui.empty}>
                                            {t("refereeList.empty.noRoleFound", {
                                                role: coachLabel,
                                            })}
                                        </div>
                                    )}
                                </div>

                                <div style={ui.helper}>
                                    {t("refereeList.helper.availableTip", {
                                        role: coachLabel,
                                    })}
                                </div>
                            </div>
                        </div>

                        <div style={ui.colCard}>
                            <div style={ui.colHeader}>
                                <div style={ui.colTitleWrap}>
                                    <h4 style={ui.colTitle}>
                                        {t("refereeList.sections.selected", { role: coachLabel })}
                                    </h4>
                                    <span style={ui.smallBadge}>
                                        {selectedReferees?.length || 0}
                                    </span>
                                </div>
                            </div>

                            <div style={ui.colBody}>
                                <div style={ui.list}>
                                    {selectedReferees?.length ? (
                                        selectedReferees.map((referee, idx) => (
                                            <div
                                                key={referee.id}
                                                style={{
                                                    ...ui.row,
                                                    ...(idx === 0 ? ui.rowFirst : null),
                                                }}
                                            >
                                                <div style={{ minWidth: 0 }}>
                                                    <p style={ui.name}>{referee.fullName}</p>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 8,
                                                        }}
                                                    >
                                                        <StarRating rating={referee.rating} />
                                                        <span style={ui.muted}>
                                                            {t("refereeList.labels.rating")}
                                                        </span>
                                                    </div>
                                                </div>

                                                <button
                                                    type="button"
                                                    style={{ ...ui.btn, ...ui.btnRemove }}
                                                    onClick={() => removeReferee(referee)}
                                                >
                                                    <i className="fas fa-chevron-left" />
                                                    {t("refereeList.buttons.remove")}
                                                </button>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={ui.empty}>
                                            {t("refereeList.empty.noRoleSelected", {
                                                role: coachLabel,
                                            })}
                                        </div>
                                    )}
                                </div>

                                <div style={ui.helper}>
                                    {t("refereeList.helper.selectedTip", {
                                        role: coachLabel,
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                #kbola-referee-list input:focus,
                #kbola-referee-list button:focus {
                  outline: none;
                  box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                }

                @media (max-width: 991px) {
                  #kbola-referee-list .kbola-grid {
                    grid-template-columns: 1fr !important;
                  }
                }
            `}</style>
        </>
    );
}